import { useEffect, useMemo, useState } from "react";
import projectApi from '../../../../utils/api/project';
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RightContactsPanel from "./RightContactsPanel";

interface UserItem {
  id: number;
  name: string;
  status?: 'on' | 'off';
  is_active?: boolean;
}

interface NotificationType {
  id: number;
  name: string;
  is_enabled?: boolean;
  users?: UserItem[];
}

interface FormItem {
  id: number;
  name: string;
  is_primary: boolean;
}

interface NotificationsUsersList {
  forms: FormItem[];
  /** Project-level (legacy / primary form) notification config — used as fallback */
  notificationTypes: NotificationType[];
  /** Per-form notification config keyed by inspection_template_id (string keys after JSON parse) */
  byForm: Record<string, NotificationType[]>;
}

function buildStateFromTypes(types: NotificationType[]) {
  const typeEnabled: Record<number, boolean> = {};
  const userEnabledByType: Record<number, Record<number, boolean>> = {};

  types.forEach((nt) => {
    typeEnabled[nt.id] = !!nt.is_enabled;
    const perUser: Record<number, boolean> = {};
    (nt.users || []).forEach((u) => {
      perUser[u.id] = typeof u.is_active === 'boolean' ? !!u.is_active : u.status === 'on';
    });
    userEnabledByType[nt.id] = perUser;
  });

  return { typeEnabled, userEnabledByType };
}

const NotificationsProjectSetting = ({ projectId, onChange }: any) => {
  const [notificationCollapse, setNotificationCollapse] = useState(true);
  const [rawData, setRawData] = useState<Partial<NotificationsUsersList>>({});
  const [openTypeId, setOpenTypeId] = useState<number | null>(null);
  const [showRightOnMobile, setShowRightOnMobile] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);

  const [typeEnabled, setTypeEnabled] = useState<Record<number, boolean>>({});
  const [userEnabledByType, setUserEnabledByType] = useState<Record<number, Record<number, boolean>>>({});

  const [byFormTypeEnabled, setByFormTypeEnabled] = useState<Record<number, Record<number, boolean>>>({});
  const [byFormUserEnabled, setByFormUserEnabled] = useState<Record<number, Record<number, Record<number, boolean>>>>({});

  const forms: FormItem[] = useMemo(() => rawData.forms || [], [rawData.forms]);

  const activeNotificationTypes: NotificationType[] = useMemo(() => {
    if (!rawData.notificationTypes) return [];
    if (selectedFormId === null) return rawData.notificationTypes;
    return (rawData.byForm || {})[String(selectedFormId)] || rawData.notificationTypes;
  }, [rawData, selectedFormId]);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchNotificationsUsersList() {
      if (!projectId) return;
      try {
        const resp = await projectApi.notificationsUsersList(projectId);
        setRawData(resp || {});
      } catch (e) {
        setRawData({});
      }
    }
    fetchNotificationsUsersList();
  }, [projectId]);

  // ── Initialise UI state from fetched data ───────────────────────────────────
  useEffect(() => {
    if (!rawData.notificationTypes) return;

    const primaryForm = (rawData.forms || []).find(f => f.is_primary) || (rawData.forms || [])[0] || null;
    const initialFormId = primaryForm?.id ?? null;
    setSelectedFormId(initialFormId);

    // Per-form state
    const newByFormType: Record<number, Record<number, boolean>> = {};
    const newByFormUser: Record<number, Record<number, Record<number, boolean>>> = {};
    Object.entries(rawData.byForm || {}).forEach(([fIdStr, ntList]) => {
      const fId = Number(fIdStr);
      const { typeEnabled: fte, userEnabledByType: fue } = buildStateFromTypes(ntList as NotificationType[]);
      newByFormType[fId] = fte;
      newByFormUser[fId] = fue;
    });
    setByFormTypeEnabled(newByFormType);
    setByFormUserEnabled(newByFormUser);

    // Seed typeEnabled/userEnabledByType for the initial form
    if (initialFormId !== null) {
      const initialTypes = (rawData.byForm || {})[String(initialFormId)] || rawData.notificationTypes;
      const { typeEnabled: te, userEnabledByType: ue } = buildStateFromTypes(initialTypes);
      setTypeEnabled(te);
      setUserEnabledByType(ue);
      setOpenTypeId(initialTypes[0]?.id ?? null);
      emitChange(newByFormType, newByFormUser);
    } else {
      const { typeEnabled: te, userEnabledByType: ue } = buildStateFromTypes(rawData.notificationTypes);
      setTypeEnabled(te);
      setUserEnabledByType(ue);
      setOpenTypeId(rawData.notificationTypes[0]?.id ?? null);
      emitChange(newByFormType, newByFormUser);
    }
  }, [rawData.notificationTypes]);

  // ── Sync local state when tab changes ──────────────────────────────────────
  useEffect(() => {
    if (selectedFormId === null) return;
    const fId = selectedFormId;
    const fKey = String(fId);

    const te = byFormTypeEnabled[fId]
      || (rawData.byForm?.[fKey] ? buildStateFromTypes(rawData.byForm[fKey]).typeEnabled : null)
      || (rawData.notificationTypes ? buildStateFromTypes(rawData.notificationTypes).typeEnabled : {});
    setTypeEnabled(te);

    const ue = byFormUserEnabled[fId]
      || (rawData.byForm?.[fKey] ? buildStateFromTypes(rawData.byForm[fKey]).userEnabledByType : null)
      || (rawData.notificationTypes ? buildStateFromTypes(rawData.notificationTypes).userEnabledByType : {});
    setUserEnabledByType(ue);
  }, [selectedFormId]);

  // ── Emit helpers ────────────────────────────────────────────────────────────
  const emitChange = (
    bfte: Record<number, Record<number, boolean>>,
    bfue: Record<number, Record<number, Record<number, boolean>>>,
  ) => {
    try {
      const byForm: Record<number, { types: Record<number, boolean>; userStatusByType: Record<number, Record<number, boolean>> }> = {};
      Object.keys(bfte).forEach(fIdStr => {
        const fId = Number(fIdStr);
        byForm[fId] = {
          types: bfte[fId] || {},
          userStatusByType: bfue[fId] || {},
        };
      });
      onChange && onChange({ byForm });
    } catch (_) {}
  };

  // ── Toggle handlers ─────────────────────────────────────────────────────────
  const handleNotificationToggle = (typeId: number) => {
    if (selectedFormId === null) return;
    const fId = selectedFormId;
    setByFormTypeEnabled(prev => {
      const prevForm = prev[fId] || { ...typeEnabled };
      const updated = { ...prev, [fId]: { ...prevForm, [typeId]: !prevForm[typeId] } };
      setTypeEnabled(updated[fId]);
      emitChange(updated, byFormUserEnabled);
      return updated;
    });
  };

  const handleToggleAll = (typeId: number, next: boolean) => {
    if (selectedFormId === null) return;
    const selType = activeNotificationTypes.find(t => t.id === typeId);
    if (!selType) return;

    const updatedUserMap: Record<number, boolean> = {};
    (selType.users || []).forEach(u => { updatedUserMap[u.id] = next; });

    const fId = selectedFormId;
    setByFormUserEnabled(prev => {
      const prevForm = prev[fId] || { ...userEnabledByType };
      const updated = {
        ...prev,
        [fId]: { ...prevForm, [typeId]: { ...(prevForm[typeId] || {}), ...updatedUserMap } },
      };
      setUserEnabledByType(updated[fId]);
      emitChange(byFormTypeEnabled, updated);
      return updated;
    });
  };

  const handleToggleUser = (typeId: number, userId: number) => {
    if (selectedFormId === null) return;
    const fId = selectedFormId;
    setByFormUserEnabled(prev => {
      const prevForm = prev[fId] || { ...userEnabledByType };
      const prevType = prevForm[typeId] || {};
      const updated = {
        ...prev,
        [fId]: { ...prevForm, [typeId]: { ...prevType, [userId]: !prevType[userId] } },
      };
      setUserEnabledByType(updated[fId]);
      emitChange(byFormTypeEnabled, updated);
      return updated;
    });
  };

  const handleRowClick = (typeId: number) => {
    setOpenTypeId(prev => {
      const isSame = prev === typeId;
      if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) {
        setShowRightOnMobile(isSame ? !showRightOnMobile : true);
      }
      return typeId;
    });
  };

  const handleFormTabClick = (formId: number) => {
    setSelectedFormId(formId);
    setOpenTypeId(activeNotificationTypes[0]?.id ?? null);
    setSearchQuery('');
  };

  const currentTypeEnabled = typeEnabled;
  const currentUserEnabled = userEnabledByType;

  // SW2-765 AC5: when the backend returns no manageable notification types for
  // this user's role (e.g. BMP Contractor — "no access"), hide the whole section
  // instead of showing an empty panel. `undefined` means still loading.
  if (rawData.notificationTypes?.length === 0) return null;

  return (
    <div className={`notifications-project-setting ${showRightOnMobile ? 'notifications-project-setting--show-right' : ''}`}>
      <div
        className="notifications-project-setting__header"
        onClick={() => setNotificationCollapse(!notificationCollapse)}
      >
        <h3>Notifications</h3>
        <i
          className={notificationCollapse ? "fa fa-chevron-down" : "fa fa-chevron-up"}
          aria-hidden="true"
        />
      </div>
      <hr />

      {notificationCollapse && (
        <>
          {forms.length > 0 && (
            <div className="notifications-project-setting__form-tabs">
              {forms.map(form => (
                <button
                  key={form.id}
                  className={`notifications-project-setting__form-tab ${selectedFormId === form.id ? 'active' : ''}`}
                  onClick={() => handleFormTabClick(form.id)}
                  type="button"
                >
                  {form.name}
                  {form.is_primary && (
                    <span className="notifications-project-setting__form-tab-badge">Primary</span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="notifications-project-setting__container">
            <div className="notifications-project-setting__left">
              {(activeNotificationTypes || []).map((type) => {
                const isActive = openTypeId === type.id;
                const currentType = activeNotificationTypes?.find(t => t.id === type.id) || null;

                return (
                  <div key={type.id} className={`notifications-project-setting__item ${isActive ? 'active' : ''}`}>
                    <div
                      className={`notifications-project-setting__row ${isActive ? 'active' : ''}`}
                      onClick={() => handleRowClick(type.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleRowClick(type.id);
                      }}
                    >
                      <span style={{ display: "flex", gap: "5px" }}>
                        {type.name}
                        <Tooltip
                          title='Disabling this notification type will prevent any notifications from being sent for this project. Individual user settings will be preserved.'
                          arrow
                          enterTouchDelay={0}
                        >
                          <span><FontAwesomeIcon icon="question-circle" size="sm" color="#0d2330" /></span>
                        </Tooltip>
                      </span>
                      <label
                        className="switch"
                        onClick={(e) => { e.stopPropagation(); }}
                      >
                        <input
                          type="checkbox"
                          checked={!!currentTypeEnabled[type.id]}
                          onChange={() => handleNotificationToggle(type.id)}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>

                    <div className={`notifications-project-setting__right notifications-project-setting__right--mobile ${isActive && showRightOnMobile ? 'show' : ''}`}>
                      <RightContactsPanel
                        type={currentType}
                        typeEnabled={!!currentTypeEnabled[type.id]}
                        userEnabled={currentUserEnabled[type.id] || {}}
                        onToggleAll={(next) => handleToggleAll(type.id, next)}
                        onToggleUser={(userId) => handleToggleUser(type.id, userId)}
                        searchQuery={searchQuery}
                        onSearchChange={(val) => setSearchQuery(val)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="notifications-project-setting__spacer" aria-hidden="true" />

            <div className="notifications-project-setting__right notifications-project-setting__right--desktop">
              <RightContactsPanel
                type={activeNotificationTypes?.find(t => t.id === openTypeId) || null}
                typeEnabled={openTypeId != null ? !!currentTypeEnabled[openTypeId] : false}
                userEnabled={openTypeId != null ? (currentUserEnabled[openTypeId] || {}) : {}}
                onToggleAll={(next) => { if (openTypeId != null) handleToggleAll(openTypeId, next); }}
                onToggleUser={(userId) => { if (openTypeId != null) handleToggleUser(openTypeId, userId); }}
                searchQuery={searchQuery}
                onSearchChange={(val) => setSearchQuery(val)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsProjectSetting;
